/* ════════════════════════════════════════════════════════════
   triQUIP RUNNING INTELLIGENCE — SHOPIFY STOREFRONT API BRIDGE
   Replaces the hardcoded `const PRODUCTS = [...]` and
   `const COLOUR_VARIANTS = {...}` arrays with real store data.

   HOW TO USE:
   1. Fill in SHOPIFY_DOMAIN and STOREFRONT_TOKEN below.
   2. Delete the old `const PRODUCTS = [ ... ];` and
      `const COLOUR_VARIANTS = { ... };` blocks from the page.
   3. Paste this whole file in their place.
   4. Replace the existing:
        window.addEventListener('DOMContentLoaded', () => { ... });
      with the version at the bottom of this file, which waits for
      the fetch to finish before rendering anything.
════════════════════════════════════════════════════════════ */

// IMPORTANT: use your PRIMARY/custom domain here, not the .myshopify.com backend
// domain. Calling {shop}.myshopify.com/api/... from a page hosted on your own
// custom domain triggers a CORS block (the browser sees them as different
// origins). Since this page is served from your own domain, calling the
// Storefront API through that same domain makes it a same-origin request —
// no CORS issue at all.
const SHOPIFY_DOMAIN     = 'triquipsports.com'; // your storefront's own domain
const STOREFRONT_TOKEN   = 'e10556d11226466274ec3aef7a8193d8';
const STOREFRONT_VERSION = '2026-01'; // bump periodically per Shopify's release schedule

// These populate once the fetch below completes.
let PRODUCTS = [];
const PRODUCT_COLOUR_MAP = {}; // { [productId]: ['Colour A','Colour B', ...] }

// Product Type (Shopify native field) -> one of the 5 category keys the page uses.
// Real store data uses varied, specific values (e.g. "Men Running Shoes",
// "Women Compression tights", "Smart Watch", "Apparel & Accessories") rather
// than exact single words, so this matches on keywords rather than requiring
// an exact string. Order matters: more specific checks run before the broad
// "apparel" catch-all so e.g. "Men Compression Tights" doesn't get miscategorized.
function classifyProductType(productType) {
  const t = (productType || '').toLowerCase();
  if (t.includes('shoe')) return 'shoes';
  if (t.includes('watch') || t.includes('wearable') || t.includes('gps') || t.includes('tracker')) return 'wearables';
  if (t.includes('compression')) return 'compression';
  if (t.includes('apparel') || t.includes('top') || t.includes('tight') || t.includes('short') || t.includes('jacket') || t.includes('hoodie') || t.includes('legging') || t.includes('trouser')) return 'apparel';
  return 'gear'; // fallback for accessories, calf sleeves, hydration, etc.
}

// Gender: checked against BOTH productType and tags, not just one, since real
// catalog data occasionally has mismatches (e.g. a product titled/tagged
// "Women" but with Product Type still saying "Men ..."). If either source
// says "women," it wins — better to trust the more specific signal than let
// a stale Product Type field misclassify a product's gender fit.
function extractGender(node) {
  const pt = (node.productType || '').toLowerCase();
  const tags = (node.tags || []).map(t => t.toLowerCase());
  const hasWomenSignal = pt.includes('women') || tags.includes('women');
  const hasMenSignal = (pt.includes('men') && !pt.includes('women')) || tags.includes('men');
  if (hasWomenSignal) return 'women';
  if (hasMenSignal) return 'men';
  return 'unisex'; // no signal either way -> shown regardless of the shopper's gender selection
}


const RUNNING_PRODUCTS_QUERY = `
query RunningProducts($first: Int!, $after: String) {
  products(first: $first, after: $after, query: "tag:'sport:running'") {
    pageInfo { hasNextPage endCursor }
    edges {
      node {
        id
        handle
        title
        vendor
        productType
        onlineStoreUrl
        tags
        featuredImage { url altText }
        variants(first: 20) {
          edges {
            node {
              id
              availableForSale
              price { amount }
              selectedOptions { name value }
            }
          }
        }
        metafields(identifiers: [
          { namespace: "custom", key: "stage" },
          { namespace: "custom", key: "discipline" },
          { namespace: "custom", key: "goal" },
          { namespace: "custom", key: "constraint_safe" },
          { namespace: "custom", key: "pronation_support" },
          { namespace: "custom", key: "budget_tier" },
          { namespace: "custom", key: "why" },
          { namespace: "custom", key: "curator" },
          { namespace: "custom", key: "rating" }
        ]) {
          key
          value
        }
      }
    }
  }
}`;

function getMeta(node, key) {
  const m = node.metafields.find(mf => mf && mf.key === key);
  return m ? m.value : null;
}

// Shopify stores "List of single line text" metafields as a JSON string, e.g. '["new","building"]'
function getMetaList(node, key) {
  const raw = getMeta(node, key);
  if (!raw) return [];
  try { return JSON.parse(raw); } catch { return []; }
}

async function fetchShopifyPage(after) {
  const res = await fetch(`https://${SHOPIFY_DOMAIN}/api/${STOREFRONT_VERSION}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
    },
    body: JSON.stringify({
      query: RUNNING_PRODUCTS_QUERY,
      variables: { first: 50, after: after || null },
    }),
  });
  if (!res.ok) throw new Error(`Storefront API error: ${res.status}`);
  const json = await res.json();
  if (json.errors) throw new Error(json.errors.map(e => e.message).join('; '));
  return json.data.products;
}

function transformNode(node) {
  const id = node.handle; // human-readable, stable, used as the product's id throughout the page
  const cat = classifyProductType(node.productType);
  const gender = extractGender(node);
  const variants = node.variants.edges.map(e => e.node);
  const firstAvailable = variants.find(v => v.availableForSale) || variants[0];
  const price = firstAvailable ? Math.round(parseFloat(firstAvailable.price.amount)) : 0;

  // Native Shopify "Colour" variant option -> populates PRODUCT_COLOUR_MAP
  const colours = [...new Set(
    variants
      .map(v => v.selectedOptions.find(o => /colour|color/i.test(o.name))?.value)
      .filter(Boolean)
  )];
  if (colours.length > 1) PRODUCT_COLOUR_MAP[id] = colours;

  return {
    id,
    cat,
    gender,
    vendor: node.vendor,
    name: node.title,
    price,
    rating: parseFloat(getMeta(node, 'rating')) || 4.5,
    stage: getMetaList(node, 'stage'),
    discipline: getMetaList(node, 'discipline'),
    goal: getMetaList(node, 'goal'),
    constraintSafe: getMetaList(node, 'constraint_safe'),
    pronationSupport: getMetaList(node, 'pronation_support'),
    budgetTier: getMetaList(node, 'budget_tier'),
    why: getMeta(node, 'why') || '',
    curator: getMeta(node, 'curator') === 'true',
    url: node.onlineStoreUrl || `https://${SHOPIFY_DOMAIN.replace('.myshopify.com', '')}.com/products/${node.handle}`,
    image: node.featuredImage?.url || '',
    colour: colours[0] || null,
  };
}

async function loadRunningProducts() {
  let after = null;
  let all = [];
  do {
    const page = await fetchShopifyPage(after);
    all = all.concat(page.edges.map(e => e.node));
    after = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
  } while (after);

  PRODUCTS = all.map(transformNode);
}

function hasColourVariants(productId) {
  return PRODUCT_COLOUR_MAP[productId] && PRODUCT_COLOUR_MAP[productId].length > 1;
}
function getOtherColours(productId, currentColour) {
  if (!PRODUCT_COLOUR_MAP[productId]) return [];
  return PRODUCT_COLOUR_MAP[productId].filter(c => c !== currentColour);
}

/* ════════════════════════════════════════════════════════════
   PAGE INIT — waits for the Shopify fetch before rendering.
   Replaces the old bare `window.addEventListener('DOMContentLoaded', ...)`
════════════════════════════════════════════════════════════ */
window.addEventListener('DOMContentLoaded', async () => {
  // Render the page chrome FIRST and unconditionally — nav, hero, and the
  // recommendation/profile-builder questions don't depend on Shopify data
  // being available, so a failed product fetch should never blank these out.
  renderSportChrome();
  renderTicker();
  checkInviteState();
  initReturningCustomer();

  const grid = document.getElementById('catGridInner');
  if (grid) grid.innerHTML = '<div style="padding:40px;text-align:center;color:var(--dm)">Loading products…</div>';

  try {
    await loadRunningProducts();
  } catch (err) {
    console.error('Failed to load products from Shopify:', err);
    if (grid) grid.innerHTML = '<div style="padding:40px;text-align:center;color:var(--dm)">Couldn\'t load products right now — check the Storefront token and product tags. See console for details.</div>';
    return;
  }

  // These three depend on PRODUCTS being populated, so they run only after a successful fetch.
  renderCuratorRail();
  renderActiveTags();
  renderGrid();
});